<template>
  <!-- :class="{'transition-all hover:shadow-card-focus hover:scale-105 hover:-rotate-1': !store.touchDevice}" -->
  <router-link
    v-if="event"
    v-press
    @click="store.openType = 'popIn'"
    :to="church ? `/network/${church.id}/events/${event.id}` : `/events/${event.id}`"
    class="w-full transition-colors"
    :class="isEventNew
      ? 'flex flex-col xs:flex-row items-stretch gap-4 p-2 border border-gray-200 dark:bg-transparent dark:border-white/10 my-4 rounded-lg'
      : isEventNewList
        ? 'flex items-start gap-4 py-4'
        : homepage
          ? 'flex flex-col items-stretch'
          : 'flex flex-col sm:flex-row items-start gap-3 sm:py-3 sm:px-3 hover:bg-gray-50 dark:hover:bg-gray-800/60'
    "
  >
    <!-- Left image -->
    <div
      class="shrink-0 overflow-hidden bg-gray-200 dark:bg-gray-800 relative"
      :class="isEventNew
        ? 'w-44 h-28 rounded-lg ring-1 ring-gray-100/80'
        : isEventNewList
          ? 'w-28 h-20 rounded-lg ring-0'
          : homepage
            ? 'w-full aspect-[16/9] rounded-none'
            : 'rounded-t-xl sm:rounded-lg ring-1 ring-gray-100/80 w-full sm:w-60 h-40'
      "
    >
      <!-- HAP-4166 — client asked for the event artwork to fill (cover), not letterbox
           (contain), so the card reads as one clean image with no dead bars or extra layers. -->
      <div
        v-if="event.bannerImage?.url"
        v-img-fade="event.bannerImage?.url"
        class="w-full h-full relative bg-no-repeat bg-cover"
        :class="positionIndex?.[event?.backgroundPosition] || 'bg-center'"
        :style="`background-image: url(${event.bannerImage?.url})`"
        :role="homepage ? 'img' : null"
        :aria-label="homepage ? event.title : null"
      >
        <!-- Title image overlay (KEEP THIS) -->
        <div
          v-if="event.titleImage?.url"
          class="absolute inset-0 bg-center bg-contain bg-no-repeat"
          :style="`background-image: url(${event.titleImage?.url})`"
        />
        <div class="hidden dark:block absolute inset-0 bg-gray-800 opacity-20" />
      </div>

      <div v-else class="w-full h-full">
        <Map
          v-if="map"
          class="w-full h-full"
          :overlay="true"
          :address="event.address"
          :zoom="4"
          :openMaps="false"
        />
        <div v-else class="w-full h-full bg-gray-300 dark:bg-gray-700" />
      </div>
    </div>

    <!-- RIGHT SIDE -->
    <div
      class="flex-1 min-w-0 flex flex-col"
      :class="isEventNew ? 'py-1 pr-2' : (isEventNewList ? 'pt-0.5' : (homepage ? 'p-4 sm:p-5 space-y-2' : 'p-3 space-y-2 h-20'))"
    >
      <!-- EVENT NEW CARD LAYOUT (featured add-ons card) -->
      <template v-if="isEventNew">
        <!-- Title -->
        <div class="text-[17px] font-semibold text-gray-900 dark:text-white leading-tight">
          {{ event.title }}
        </div>

        <!-- Meta line (subhead • date) -->
        <div class="text-sm text-gray-600 dark:text-gray-400 leading-snug font-normal flex items-center gap-2">
          <template v-if="subhead">
            <span>{{ subhead }}</span>
            <span class="text-gray-300 dark:text-white/20">•</span>
          </template>
          <span>{{ dateRange }}</span>
        </div>

        <!-- Spacer to push price row down nicely -->
        <div class="flex-1" />

        <!-- Dashed divider -->
        <div class="mt-3 border-t border-dashed border-gray-200/80 dark:border-white/10" />

        <!-- Bottom row: price left, Learn More right -->
        <div class="mt-2 flex items-center justify-start sm:justify-between text-sm">
          <div class="text-gray-900 dark:text-white font-medium">
            {{ price || event?.price || event?.cost || event?.registrationPrice || '' }}
          </div>

          <div class="text-ncfic-blue-700/90 dark:text-ncfic-blue-300 font-medium inline-flex items-center gap-2">
            <span>Learn More</span>
            <svg class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M7.21 14.77a.75.75 0 0 1 .02-1.06L10.94 10 7.23 6.29a.75.75 0 1 1 1.06-1.06l4.24 4.24a.75.75 0 0 1 0 1.06l-4.24 4.24a.75.75 0 0 1-1.06.02z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </div>
      </template>

      <!-- EVENT NEW LIST (matches screenshot exactly: title, optional subhead, date on its own line) -->
      <template v-else-if="isEventNewList">
        <div class="min-w-0">
          <div class="text-[18px] font-semibold text-gray-900 dark:text-white leading-tight truncate">
            {{ event.title }}
          </div>

          <div
            v-if="subhead"
            class="mt-1 text-[15px] text-gray-600 dark:text-gray-400 leading-snug truncate font-normal"
          >
            {{ subhead }}
          </div>

          <div class="mt-1 text-[15px] text-gray-600 dark:text-gray-400 leading-snug font-normal">
            {{ dateRange }}
          </div>
        </div>
      </template>

      <!-- NORMAL EVENT CARD (unchanged behavior) -->
      <template v-else>
        <People
          :class="isEventNew ? 'hidden' : ''"
          :people="event.speakers"
          :limit="3"
        />

        <div
          :class="isEventNew ? '' : 'pt-4'"
          class="text-[17px] flex font-semibold text-gray-900 dark:text-white leading-tight font-semibold"
        >
          {{ event.title }}
        </div>

        <div class="flex-1 flex items-center text-sm text-gray-600 dark:text-gray-400 leading-snug font-normal">
          <template v-if="subhead">{{ subhead }}</template>
          <template v-else-if="event.address?.address != 'online' && !event.title?.toLowerCase()?.includes('stream')">
            {{ event.address?.city }}, {{ event.address?.state }}
          </template>
          <template v-else>
            Virtual Event
          </template>
        </div>

        <div class="text-sm text-gray-600 dark:text-gray-500 leading-snug font-normal">
          {{ dateRange }}
        </div>
      </template>
    </div>
  </router-link>

  <div
    v-else
    class="flex flex-col xs:flex-row justify-center items-center relative overflow-hidden shadow-card hover:shadow-card-focus bg-white rounded-lg xs:space-x-4 sm:space-x-8 md:space-x-16 xs:px-4 sm:px-8 md:px-16"
  >
    <!-- <div :style="`background-image: url(${event.bannerImage?.url})`" class="absolute inset-0 w-full h-full opacity-25 bg-center bg-cover" /> -->
    <div class="shrink-0 flex items-center justify-center xs:py-4 sm:py-8 md:py-16 w-full xs:w-auto">
      <div class="w-full xs:w-32 h-32 sm:w-48 sm:h-48 xs:rounded-full p-4 md:p-8 bg-gray-100 dark:bg-gray-600" />
    </div>
    <div class="w-full">
      <div class="p-4 xs:py-4 sm:py-8 md:py-16 flex flex-col justify-between text-left flex-grow w-full max-w-full dark:text-gray-400">
        <div>
          <div class="flex xs:hidden sm:flex flex-wrap justify-center xs:justify-start truncate -mt-8 xs:mt-0 mb-2 p-2">
            <People :limit="4" />
          </div>
          <div class="h-4 md:h-8 rounded-full bg-gray-100 dark:bg-gray-600 mt-4" />
          <div class="py-2 rounded h-2 bg-gray-100 dark:bg-gray-600 mt-4 w-2/5" />
          <div class="dark:bg-ncfic-blue-800/50 bg-ncfic-blue-200 rounded h-2 w-1/3 mt-4" />
        </div>
      </div>
    </div>
  </div>
</template>



<script>
import People from '../layout/people.vue'
import Map from '../layout/map.vue'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  props: {
    people: { type: Array, default: () => [] },
    limit: { type: Number, default: 3 },
    event: {},
    map: { default: true },
    parentMapVisible: { type: Boolean },
    subhead: {},
    price: { type: String, default: '' },
    church: {},
    homepage: { type: Boolean },
    variant: { type: String, default: '' },
  },
  inject: ['store', 'axios'],
  components: { People, Map },
  data() {
    return {
      positionIndex: {
        tl: 'bg-left-top',
        top: 'bg-top',
        rt: 'bg-right-top',
        left: 'bg-left',
        center: 'bg-center',
        right: 'bg-right',
        bl: 'bg-left-bottom',
        bottom: 'bg-bottom',
        br: 'bg-right-bottom',
      },
    }
  },
  computed: {
    isEventNew() {
      return this.variant === 'eventNew'
    },
    isEventNewList() {
      return this.variant === 'eventNewList'
    },
    national() {
      return this.event?.scale === 'national'
    },
    dateRange() {
      if (this.event.dateTBD) return 'Date to be determined'
      if (new Date(this.event.startDate) < new Date())
        return this.dayjs(this.event.startDate).tz('America/New_York').format('MMMM D, YYYY')

      const start = dayjs(this.event.startDate).tz('America/New_York')
      const end = dayjs(this.event.endDate).tz('America/New_York')

      const sameDay = start.isSame(end, 'day')
      const sameMonth = start.isSame(end, 'month')

      let dateRange = ''

      if (sameDay) {
        dateRange = start.format('MMMM D, YYYY')
      } else if (sameMonth) {
        dateRange = `${start.format('MMMM D')}–${end.format('D')}, ${end.format('YYYY')}`
      } else {
        dateRange = `${start.format('MMMM D')}–${end.format('MMMM D')}, ${end.format('YYYY')}`
      }

      return dateRange
    },
  },
  methods: {
    dayjs,
    async updatePosition(backgroundPosition) {
      let response = await this.axios.put(`https://api.churchandfamilylife.com/events/${this.event.id}`, { backgroundPosition })
      this.event.backgroundPosition = response?.data?.response?.backgroundPosition
    },
  },
}
</script>
